export type PDFSection = {
  key: string;
  title: string;
  description: string;
  info?: string;
};

export type PDFAdapterKey =
  | 'datahub'
  | 'multiverse'
  | 'creditLine'
  | 'datahubForFund'
  | 'balance';

type PDFAdapterValue = {
  title: string;
  sections: PDFSection[];
};

export const PDFAdapter: Record<PDFAdapterKey, PDFAdapterValue> = {
  datahub: {
    title: 'Reporte de Datahub',
    sections: [
      {
        key: 'client',
        title: 'Cliente',
        description: 'Información general del cliente, blacklist y socios',
      },
      {
        key: 'invoice',
        title: 'Factura',
        description: 'Información de ventas y compras mensuales.',
      },
      {
        key: 'factoring',
        title: 'Factoring',
        description: 'Factoring mensual. Principales pagadores y factorings',
      },
      {
        key: 'iva',
        title: 'Carpeta Tributaria',
        description:
          'Información de la carpeta tributaria (F29) e IVA acomulado',
      },
      {
        key: 'tgr',
        title: 'TGR',
        description:
          'Deudas fiscales, convenios y cobranza judicial (Procedimientos)',
      },
      {
        key: 'publicMarket',
        title: 'Mercado Público',
        description:
          'Información de mercado público. Ordenes de compra y licitaciones ',
      },
      {
        key: 'debt',
        title: 'Deuda',
        description: 'Deudas principales del cliente y montos asociados.',
        info: 'Información de la ultima consulta realizada al cliente en cuestión. En caso que nunca se haya consultado, se realizara una nueva consulta.',
      },
      {
        key: 'buro',
        title: 'Buro de Crédito',
        description:
          'Información de buro de crédito. Principales deudores y deudas.',
        info: 'Información de la ultima consulta realizada al cliente en cuestión. En caso que nunca se haya consultado, se realizara una nueva consulta.',
      },
      {
        key: 'socialSecurityArrear',
        title: 'Mora Previsional',
        description: 'Mora previsional del cliente y montos asociados.',
      },
      {
        key: 'laborLawFines',
        title: 'Multas Laborales',
        description: 'Multas laborales del cliente y montos asociados.',
      },
    ],
  },
  multiverse: {
    title: 'Reporte para Fondos',
    sections: [
      {
        key: 'corePlacementKpis',
        title: 'KPIs Bases',
        description: 'Colocación, documentos activos y promedio de documento.',
      },
      {
        key: 'coreOperationsKpis',
        title: 'KPIs Operaciones',
        description: 'Tasas promedios y tiempo promedio de financiamiento.',
      },
      {
        key: 'corePlacementTimeline',
        title: 'Colocación en el tiempo',
        description: 'Colocación en el rango de tiempo seleccionado.',
      },
      {
        key: 'corePlacementByType',
        title: 'Colocación por Tipo',
        description: 'Colocación por tipo de operación.',
      },
      {
        key: 'coreArrear',
        title: 'Mora',
        description: 'Estado de mora del monto colocado.',
      },
      {
        key: 'coreFundingSource',
        title: 'Fuente de Financiamiento',
        description: 'Fuente de financiamiento. (Propio / Fondos)',
      },
      {
        key: 'coreClientES',
        title: 'Sector Económico Clientes',
        description: 'Monto colocado por sector económico de los clientes.',
      },
      {
        key: 'coreClientSS',
        title: 'Segmento de Venta Clientes',
        description: 'Monto colocado por segmento de venta de los clientes.',
      },
      {
        key: 'coreStakeholderES',
        title: 'Sector Económico Deudores',
        description: 'Monto colocado por sector económico de los deudores.',
      },
      {
        key: 'coreStakeholderSS',
        title: 'Segmento de Venta Deudores',
        description: 'Monto colocado por segmento de venta de los deudores.',
      },
      {
        key: 'reportCessionTimeline',
        title: 'Cesión en el tiempo',
        description:
          'Cesión al fondo seleccionado y acomulados en otros fondos.',
      },
      {
        key: 'reportCessionAmount',
        title: 'Monto de Cesión',
        description:
          'Desglose de monto del fondo seleccionado y acomulados en otros fondos.',
      },
      {
        key: 'reportKpis',
        title: 'KPIs Fondo',
        description:
          'KPIs relación con el fondo seleccionado. (Tasa promedio, Promedio cesión, Promedio operación, Financiamiento promedio) ',
      },
      {
        key: 'reportClientES',
        title: 'Sector Económico Clientes',
        description: 'Monto cedido por sector económico de los clientes.',
      },
      {
        key: 'reportClientSS',
        title: 'Segmento de Venta Clientes',
        description: 'Monto cedido por segmento de venta de los clientes.',
      },
      {
        key: 'reportStakeholderES',
        title: 'Sector Económico Deudores',
        description: 'Monto cedido por sector económico de los deudores.',
      },
      {
        key: 'reportStakeholderSS',
        title: 'Segmento de Venta Deudores',
        description: 'Monto cedido por segmento de venta de los deudores.',
      },
      {
        key: 'reportClientAES',
        title: 'Mora Sector Económico Clientes',
        description:
          'Monto cedido en mora por sector económico de los clientes.',
      },
      {
        key: 'reportClientASS',
        title: 'Mora Segmento de Venta Clientes',
        description:
          'Monto cedido en mora por segmento de venta de los clientes.',
      },
      {
        key: 'reportStakeholderAES',
        title: 'Mora Sector Económico Deudores',
        description:
          'Monto cedido en mora por sector económico de los deudores.',
      },
      {
        key: 'reportStakeholderASS',
        title: 'Mora Segmento de Venta Deudores',
        description:
          'Monto cedido en mora por segmento de venta de los deudores.',
      },
    ],
  },
  creditLine: {
    title: 'Reporte de Crédito',
    sections: [
      {
        key: 'creditLineMetadata',
        title: 'Linea de Crédito',
        description: 'Información general de la línea de crédito.',
      },
      {
        key: 'creditLineEvaluation',
        title: 'Evaluación',
        description: 'Evaluaciones del motor de riesgo.',
      },
      {
        key: 'datahub',
        title: 'Datahub',
        description: 'Información de Facturas, Factoring, IVA y TGR.',
      },
      {
        key: 'balance',
        title: 'Balance',
        description: 'Vaciado de balances y metricas asociadas.',
      },
      {
        key: 'scoring',
        title: 'Scoring',
        description: 'Scoring del cliente.',
      },
    ],
  },
  datahubForFund: {
    title: 'Reporte de Datahub para Fondos',
    sections: [
      {
        key: 'client',
        title: 'Cliente',
        description: 'Información general del cliente, blacklist y socios',
      },
      {
        key: 'invoice',
        title: 'Factura',
        description:
          'Información de ventas y compras mensuales. Principales clientes y proveedores.',
      },
      {
        key: 'factoring',
        title: 'Factoring',
        description: 'Factoring mensual. Principales pagadores y factorings',
      },
      {
        key: 'iva',
        title: 'Carpeta Tributaria',
        description:
          'Información de la carpeta tributaria (F29) e IVA acomulado',
      },
      {
        key: 'tgr',
        title: 'TGR',
        description:
          'Deudas fiscales, convenios y cobranza judicial (Procedimientos)',
      },
      {
        key: 'publicMarket',
        title: 'Mercado Público',
        description:
          'Información de mercado público. Ordenes de compra y licitaciones ',
      },
      {
        key: 'socialSecurityArrear',
        title: 'Mora Previsional',
        description: 'Mora previsional del cliente y montos asociados.',
      },
      {
        key: 'laborLawFines',
        title: 'Multas Laborales',
        description: 'Multas laborales del cliente y montos asociados.',
      },
    ],
  },
  balance: {
    title: 'Balance',
    sections: [
      {
        key: 'balance',
        title: 'Balance',
        description: 'Balance del cliente por año.',
      },
      {
        key: 'metrics',
        title: 'Métricas',
        description: 'Métricas del cliente por año.',
      },
    ],
  },
};
